import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useJobStatus } from '@/hooks/api/useReadings';
import { useApiErrorToast } from '@/hooks/useApiErrorToast';
import { useNow } from '@/hooks/useNow';
import { useToast } from '@/hooks/useToast';
import { t } from '@/i18n';
import {
  getJobEstimateMs,
  getJobStartedAt,
  getReadingProgress,
  getRemainingMs,
  pickLoadingStage,
} from '@/utils/readingProgress';
import styles from './LoadingScreen.module.css';

// The fill's CSS transition matches this, so the bar glides between ticks.
const PROGRESS_TICK_MS = 1000;
// Lets the bar visibly fill to 100% before the result replaces the screen;
// matches the `.fillDone` transition in LoadingScreen.module.css.
const COMPLETE_FILL_MS = 400;

/**
 * Crossfades between captions: the previous one fades out while the new one
 * fades in on the same spot, so the layout never jumps.
 */
function StageCaption({ text }: { text: string }) {
  const [shown, setShown] = useState(text);
  const [leaving, setLeaving] = useState<string | null>(null);
  if (text !== shown) {
    setLeaving(shown);
    setShown(text);
  }

  return (
    <div className={styles.stage} aria-live="polite">
      {leaving !== null && (
        <span
          key={`out-${leaving}`}
          className={styles.stageOut}
          aria-hidden
          onAnimationEnd={() => setLeaving(null)}
        >
          {leaving}
        </span>
      )}
      <span key={shown} className={styles.stageIn}>
        {shown}
      </span>
    </div>
  );
}

export function LoadingScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const jobId = params.get('jobId');
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const onApiError = useApiErrorToast();
  const { data, error, dataUpdatedAt } = useJobStatus(jobId);
  const now = useNow(PROGRESS_TICK_MS);

  const estimatedMs = getJobEstimateMs(data);
  const elapsedMs = data ? now - getJobStartedAt(data, dataUpdatedAt) : 0;
  const progress = getReadingProgress(data?.status, elapsedMs, estimatedMs);
  const percent = Math.round(progress * 100);
  const isDone = data?.status === 'completed';
  const remainingMs = getRemainingMs(elapsedMs, estimatedMs);
  const remainingLabel = isDone
    ? t.loading.done
    : data && remainingMs !== null
      ? t.loading.remaining(remainingMs)
      : null;
  const stage = pickLoadingStage(
    elapsedMs,
    estimatedMs,
    t.loading.stages,
    t.loading.overtimeStages,
  );

  useEffect(() => {
    if (!jobId) {
      navigate('/spread', { replace: true });
    }
  }, [jobId, navigate]);

  useEffect(() => {
    if (!data) return;
    if (data.status === 'completed') {
      queryClient.invalidateQueries({ queryKey: ['users', 'me'] });
      queryClient.invalidateQueries({ queryKey: ['readings', 'list'] });
      const { readingId } = data;
      const timeout = setTimeout(() => {
        if (typeof readingId === 'number') {
          navigate(`/result?id=${readingId}`, { replace: true });
        } else {
          showToast(t.loading.resultOpenFailed, 'error');
          navigate('/', { replace: true });
        }
      }, COMPLETE_FILL_MS);
      return () => clearTimeout(timeout);
    }
    if (data.status === 'failed') {
      showToast(data.errorMessage ?? t.loading.jobFailed, 'error');
      navigate('/spread', { replace: true });
    }
  }, [data, navigate, queryClient, showToast]);

  useEffect(() => {
    if (error) {
      // A 401 means the session died, not that the status check failed —
      // let the "session expired" default win instead of masking it with a
      // custom fallback (same fix as SpreadPage's job-creation error handling).
      onApiError(
        error,
        error.status === 401 ? undefined : t.loading.jobStatusFailed,
      );
      // The job may still be running (or already done) on the backend; the
      // profile's reading history is where its result will show up.
      queryClient.invalidateQueries({ queryKey: ['readings', 'list'] });
      navigate('/profile', { replace: true });
    }
  }, [error, navigate, onApiError, queryClient]);

  return (
    <div className={styles.overlay}>
      <div className={styles.orb} aria-hidden>
        {String.fromCodePoint(0x1f52e)}
      </div>
      <div className={styles.title}>{t.loading.title}</div>
      <StageCaption text={stage} />
      <div className={styles.progress}>
        <div
          className={styles.track}
          role="progressbar"
          aria-label={t.loading.progressAria}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
        >
          <div
            className={
              isDone ? `${styles.fill} ${styles.fillDone}` : styles.fill
            }
            style={{ width: `${percent}%` }}
          />
        </div>
        <div className={styles.meta} aria-hidden>
          <span className={styles.percent}>{percent}%</span>
          {remainingLabel && (
            <span className={styles.remaining}>{remainingLabel}</span>
          )}
        </div>
      </div>
    </div>
  );
}
